import React, { useState, useEffect } from 'react';
import {
  Paper,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import {
  People as PeopleIcon,
  LocationOn as LocationIcon,
  Email as EmailIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import axios from 'axios';

function HarvestStats({ refreshTrigger }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadStats();
  }, [refreshTrigger]);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await axios.get('/api/harvest/stats', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
      setError('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!stats) {
    return null;
  }

  const { overview, locationBreakdown } = stats;

  return (
    <Box>
      {/* Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PeopleIcon color="primary" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {overview.total_contacts || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Contacts
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <LocationIcon color="secondary" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {overview.active_locations || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Locations
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <EmailIcon color="success" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {overview.contacts_with_email || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    With Email
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PersonIcon color="info" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {overview.contacts_with_collector || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    With Collector
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Location Breakdown */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Contacts by Location
        </Typography>
        
        {locationBreakdown.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
            No contacts found yet. Start adding contacts to see location statistics.
          </Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Location</TableCell>
                  <TableCell align="right">Contact Count</TableCell>
                  <TableCell align="right">Percentage</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {locationBreakdown.map((location) => {
                  const percentage = overview.total_contacts > 0 
                    ? ((location.contact_count / overview.total_contacts) * 100).toFixed(1)
                    : 0;
                  
                  return (
                    <TableRow key={location.location_name}>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Chip 
                            label={location.location_name} 
                            size="small" 
                            sx={{ mr: 1 }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="h6">
                          {location.contact_count}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {percentage}%
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Additional Insights */}
      <Grid container spacing={3} sx={{ mt: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Data Quality Insights
            </Typography>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Email Coverage: {overview.total_contacts > 0 
                  ? ((overview.contacts_with_email / overview.total_contacts) * 100).toFixed(1)
                  : 0}%
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Collector Tracking: {overview.total_contacts > 0 
                  ? ((overview.contacts_with_collector / overview.total_contacts) * 100).toFixed(1)
                  : 0}%
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recommendations
            </Typography>
            <Box>
              {overview.total_contacts === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Start collecting contacts during your Saturday evangelism outreach!
                </Typography>
              )}
              {overview.total_contacts > 0 && overview.contacts_with_email < overview.total_contacts * 0.5 && (
                <Typography variant="body2" color="text.secondary">
                  Consider collecting more email addresses for better follow-up communication.
                </Typography>
              )}
              {overview.total_contacts > 0 && overview.contacts_with_collector < overview.total_contacts * 0.8 && (
                <Typography variant="body2" color="text.secondary">
                  Track collector information to improve accountability and follow-up.
                </Typography>
              )}
              {overview.total_contacts > 0 && locationBreakdown.length < 3 && (
                <Typography variant="body2" color="text.secondary">
                  Expand outreach to more locations for broader community impact.
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default HarvestStats;
